import { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { matchPath } from 'react-router-dom';
import classnames from 'classnames';

import { isNotEmpty, scrollToAnchor } from '~/utils';
import {
  appointmentDraftActions,
  appointmentDraftSelectors,
  journeyStateActions,
  shopsSelectors,
} from '~/api';
import { loadPartners } from '~/routes/partners/partners.actions';
import {
  errorMessagesActions,
  errorTypes,
  scrollToMessageTypeAnchor,
  EditAppointmentTitle,
  StateContext,
  states,
  withRestorableDraft,
  calendarSelectors,
  warningModalSelectors,
  TitleWithSubtitle,
  envConfigSelectors,
} from '~/components';
import {
  saveTemporaryAppointment,
  setEditMode,
  saveTemporaryJourneyState,
} from '../../edit-appointment.actions';
import { triggerEditPageSaveEditsAnalytics } from '~/analytics/analytics.listener';

import { EditButtonsWrapper } from '~/routes/edit/common/components';
import { formatToTemporaryState } from '../../helpers';

import {
  removeTokenFromStorage,
  TEMPORARY_TOKEN_NAME,
} from '~/components/persist-token/persist-token.utils';

import { setHeadlineBackButtonConfig } from '~/ocab-services/services.actions';

import { Calendar, TimeSlots } from '~/containers';
import { countedApiCallFinished } from '~/components/loading-spinner';

import styles from '~/routes/select-date/SelectDate.scss';

const ScrollToMessageTypeAnchor = scrollToMessageTypeAnchor(({ children }) => children);

const { SLOT_DATE_NOT_SELECTED_ERROR, SLOT_TIME_NOT_SELECTED_ERROR } = errorTypes;

export class EditDateTime extends PureComponent {
  state = {
    isAjaxCompleted: false,
  };

  componentDidMount() {
    const {
      ChosenPartner,

      dispatchLoadPartners,
      dispatchApiCallFinished,
    } = this.props;

    if (ChosenPartner) {
      dispatchApiCallFinished(false);
      dispatchLoadPartners().then(() => {
        dispatchApiCallFinished(true);
      });
    }
    window.scrollTo(0, 0);
  }

  componentDidUpdate({ mainAppointmentDraft: prevMainAppointmentDraft }) {
    const {
      mainAppointmentDraft,
      relatedAppointmentTimeSlot,
      isMultipleAppointment,
      slotErrorMessages,

      dispatchRemoveSingleErrorMessage,
    } = this.props;

    if (slotErrorMessages[SLOT_DATE_NOT_SELECTED_ERROR] && mainAppointmentDraft.dateSlot) {
      dispatchRemoveSingleErrorMessage(SLOT_DATE_NOT_SELECTED_ERROR);
    }

    if (
      slotErrorMessages[SLOT_TIME_NOT_SELECTED_ERROR] &&
      mainAppointmentDraft.dateSlot !== prevMainAppointmentDraft.dateSlot
    ) {
      dispatchRemoveSingleErrorMessage(SLOT_TIME_NOT_SELECTED_ERROR);
    }

    if (slotErrorMessages[SLOT_TIME_NOT_SELECTED_ERROR] && mainAppointmentDraft.timeSlot.from) {
      if (!isMultipleAppointment && mainAppointmentDraft.timeSlot.from) {
        dispatchRemoveSingleErrorMessage(SLOT_TIME_NOT_SELECTED_ERROR);
      } else if (mainAppointmentDraft.timeSlot.from && relatedAppointmentTimeSlot.from) {
        dispatchRemoveSingleErrorMessage(SLOT_TIME_NOT_SELECTED_ERROR);
      }
    }

    if (prevMainAppointmentDraft.dateSlot === '' && mainAppointmentDraft.dateSlot) {
      scrollToAnchor('time-slot-title');
    }
  }

  selectPartnersGroup = (group) => {
    const {
      dispatchSelectPartnersGroup,
      dispatchSelectPartner,
      dispatchClearErrorMessages,
      dispatchUpdateAppointmentType,
      dispatchApiCallFinished,
      dispatchLoadPartners,
    } = this.props;

    dispatchApiCallFinished(false);
    dispatchClearErrorMessages();
    dispatchSelectPartner();
    dispatchSelectPartnersGroup(group.id);
    dispatchUpdateAppointmentType()
      .then(() => dispatchLoadPartners())
      .then(() => dispatchApiCallFinished(true));
  };

  selectPartner = (partner) => {
    const { dispatchSelectPartner } = this.props;

    if (partner !== null) {
      dispatchSelectPartner(partner);
      scrollToAnchor('chosen-partner');
    }
  };

  saveHandler = () => {
    const {
      mainAppointmentDraft,
      relatedAppointmentTimeSlot,
      isMultipleAppointment,

      onSaveDataHandler,
      dispatchResetErrorMessagesWith,
      dispatchApiCallFinished,
    } = this.props;

    if (!mainAppointmentDraft.dateSlot) {
      dispatchResetErrorMessagesWith([{ type: SLOT_DATE_NOT_SELECTED_ERROR }]);
    } else if (!mainAppointmentDraft.timeSlot.from) {
      dispatchResetErrorMessagesWith([{ type: SLOT_TIME_NOT_SELECTED_ERROR }]);
    } else if (isMultipleAppointment && !relatedAppointmentTimeSlot.from) {
      dispatchResetErrorMessagesWith([{ type: SLOT_TIME_NOT_SELECTED_ERROR }]);
    } else {
      dispatchApiCallFinished(false);
      return onSaveDataHandler()
        .then(() => this.navigateToReviewPage())
        .then(() => triggerEditPageSaveEditsAnalytics('appointments'))
        .catch(() => this.exitEditMode())
        .finally(() => dispatchApiCallFinished(true));
    }
    return Promise.resolve();
  };

  selectShop = (shop) => {
    const {
      dispatchApiCallFinished,
      dispatchSelectPartner,
      dispatchSelectShop,
      dispatchUpdateAppointmentType,
      dispatchLoadPartners,
    } = this.props;

    dispatchApiCallFinished(false);
    dispatchSelectPartner();
    dispatchSelectShop(shop);

    dispatchUpdateAppointmentType()
      .then(() => dispatchLoadPartners())
      .then(() => {
        dispatchApiCallFinished(true);
        this.setState({
          isAjaxCompleted: true,
        });
      });
  };

  navigateToReviewPage = () => {
    const { history } = this.props;
    const {
      location: { pathname },
    } = history;
    this.exitEditMode();
    const match = matchPath(pathname, { path: '/:appointmentId([0-9]+)' }) || { params: {} };
    history.pushWithToken(
      match.params.appointmentId ? `/${match.params.appointmentId}` : '/review',
    );
  };

  exitEditMode = () => {
    const {
      dispatchSetEditMode,
      dispatchSaveTemporaryAppointment,
      dispatchSaveTemporaryJourneyState,
    } = this.props;
    dispatchSetEditMode(false);
    dispatchSaveTemporaryAppointment({});
    dispatchSaveTemporaryJourneyState({});
    removeTokenFromStorage(TEMPORARY_TOKEN_NAME);
  };

  render() {
    const {
      mainAppointmentDraft,
      journeyState,
      partners,
      ChosenPartner,
      GroupsDropdown,
      config: { partnerTitle, ...restConfig },
      temporaryAppointment,
      history,
      dispatchSetEditMode,
      dispatchSetDraftAppointment,
      dispatchSaveTemporaryAppointment,
      prevPath,
      temporaryJourneyState,
      dispatchSetJourneyState,
      location,
      showWarningModal,
      title,
      subtitleComponent: Subtitle,
      ShopsDropdown,
      shopList,
      isRemoteConfigEnabled,
      dispatchSetHeadlineBackButtonConfig,
      hideEditDateTitle,
      hideGoBackButton,
      inStoreAppointmentTypeId,
      isMultipleAppointment,
    } = this.props;

    const { isAjaxCompleted } = this.state;

    const {
      appointmentType: { groups: partnersGroups = [] },
      partner: selectedPartner,
      shop: { id: selectedShopId },
      id: bookingId,
      partnersGroupId: selectedPartnersGroupId,
    } = mainAppointmentDraft;
    const partnersGroupName = (
      partnersGroups.find((partnersGroup) => partnersGroup.id === selectedPartnersGroupId) || {}
    ).name;

    return (
      <section data-testid="EditDate" data-test-ajax={isAjaxCompleted}>
        {!hideEditDateTitle && <EditAppointmentTitle />}
        <ScrollToMessageTypeAnchor forTypes={[SLOT_DATE_NOT_SELECTED_ERROR]}>
          <TitleWithSubtitle title={title} className={styles.Title} dataTest="EditDateTitle">
            {Subtitle && (
              <Subtitle
                className={styles.Subtitle}
                classNameForSubtitleWithLink={styles.SubtitleWithLink}
                secondLineClassName={styles.Subtitle__second}
                inStoreAppointmentTypeId={inStoreAppointmentTypeId}
                hideInStoreLink
              />
            )}
          </TitleWithSubtitle>
        </ScrollToMessageTypeAnchor>
        <div className={classnames({ [styles.Row]: isRemoteConfigEnabled || GroupsDropdown })}>
          {ShopsDropdown && !!shopList.length && (
            <ShopsDropdown
              selectedShopId={selectedShopId}
              shopList={shopList}
              onChange={this.selectShop}
              className={styles.Col}
            />
          )}
          {GroupsDropdown && (
            <GroupsDropdown
              partnersGroups={partnersGroups}
              selectedPartnersGroupId={selectedPartnersGroupId}
              onChange={this.selectPartnersGroup}
              className={styles.Col}
            />
          )}
          {!isMultipleAppointment && ChosenPartner && (
            <ChosenPartner
              partner={selectedPartner}
              partners={partners}
              selectPartner={this.selectPartner}
              selectedShopId={selectedShopId}
              isAppointmentTypeRemote={isRemoteConfigEnabled}
              isSeries={isRemoteConfigEnabled || !!GroupsDropdown}
              partnerTitle={partnersGroupName || partnerTitle}
              {...restConfig}
            />
          )}
        </div>
        <Calendar />
        <TimeSlots />
        <StateContext.Provider value={states.edit}>
          <EditButtonsWrapper
            temporaryDraftAppointment={temporaryAppointment}
            history={history}
            location={location}
            showWarningModal={showWarningModal}
            bookingId={bookingId}
            state={formatToTemporaryState(mainAppointmentDraft)}
            journeyState={journeyState}
            saveHandler={this.saveHandler}
            setEditMode={dispatchSetEditMode}
            saveTemporaryState={dispatchSaveTemporaryAppointment}
            saveButtonText="Save"
            prevPath={prevPath}
            temporaryJourneyState={temporaryJourneyState}
            restoreAppointmentDraft={dispatchSetDraftAppointment}
            restoreJourneyState={dispatchSetJourneyState}
            hideGoBackButton={hideGoBackButton}
            setHeadlineBackButtonConfig={dispatchSetHeadlineBackButtonConfig}
          />
        </StateContext.Provider>
      </section>
    );
  }
}

export const mapStateToProps = (state) => {
  const {
    journeyState,
    partners: { partners },
    services: { config },
    editAppointment: { temporaryAppointment, temporaryJourneyState },
    messages: {
      app: { errorMessages: slotErrorMessages },
    },
    router: { location },
    envConfig,
  } = state;

  const mainAppointmentDraft = appointmentDraftSelectors.selectAppointmentDraft(state);

  const mainAppointmentTypeId = mainAppointmentDraft.appointmentType.id;

  const relatedAppointmentDraft = appointmentDraftSelectors.selectRelatedAppointmentDraftById()(
    state,
  ) || { appointmentType: {} };
  const selectedSlotErrorMessages = calendarSelectors.selectSlotMessages(slotErrorMessages);
  const warningModalState = warningModalSelectors.selectState(state);
  const remoteConfig = envConfigSelectors.remoteConfig(mainAppointmentTypeId)(state);
  const { inStoreAppointmentTypeId } = envConfigSelectors.selectAppointmentTypeConfig(
    envConfig,
    mainAppointmentTypeId,
  );
  const chosenPartner = envConfigSelectors.selectChosenPartner(mainAppointmentTypeId)(state);
  const groupsDropdown = envConfigSelectors.selectGroupsDropdown(state);

  return {
    mainAppointmentDraft,
    relatedAppointmentTimeSlot: relatedAppointmentDraft.timeSlot,
    journeyState,
    partners,
    config,
    temporaryAppointment,
    temporaryJourneyState,
    prevPath: remoteConfig.editDatePrevPath || config.editDatePrevPath,
    slotErrorMessages: selectedSlotErrorMessages,
    location,
    showWarningModal: !!warningModalState.isOpen,
    isRemoteConfigEnabled: isNotEmpty(remoteConfig),
    title: remoteConfig.selectDateTitle || 'Choose a date for your appointment',
    subtitleComponent: remoteConfig.subtitleComponent,
    ShopsDropdown: remoteConfig.ShopsDropdown,
    shopList: shopsSelectors.selectShopList(state),
    hideEditDateTitle: envConfigSelectors.hideEditDateTitle(mainAppointmentTypeId)(state),
    hideGoBackButton: envConfigSelectors.hideEditDateGoBackButton(mainAppointmentTypeId)(state),
    inStoreAppointmentTypeId,
    ChosenPartner: chosenPartner,
    GroupsDropdown: groupsDropdown,
    isMultipleAppointment: appointmentDraftSelectors.isMultipleAppointment(state),
  };
};

const mapDispatchToProps = {
  dispatchLoadPartners: loadPartners,
  dispatchSelectPartner: appointmentDraftActions.setPartner,
  dispatchSelectShop: appointmentDraftActions.setShop,
  dispatchUpdateAppointmentType: appointmentDraftActions.updateAppointmentType,
  dispatchRemoveSingleErrorMessage: errorMessagesActions.removeSingleErrorMessage,
  dispatchResetErrorMessagesWith: errorMessagesActions.resetErrorMessagesWith,
  dispatchApiCallFinished: countedApiCallFinished,
  dispatchSetEditMode: setEditMode,
  dispatchSaveTemporaryAppointment: saveTemporaryAppointment,
  dispatchSetDraftAppointment: appointmentDraftActions.setDraftAppointment,
  dispatchSetJourneyState: journeyStateActions.setJourneyState,
  dispatchSaveTemporaryJourneyState: saveTemporaryJourneyState,
  dispatchSetHeadlineBackButtonConfig: setHeadlineBackButtonConfig,
  dispatchSelectPartnersGroup: appointmentDraftActions.setPartnersGroupId,
  dispatchClearErrorMessages: errorMessagesActions.clearErrorMessages,
};

export const ConnectedEditDateTime = withRestorableDraft(
  connect(mapStateToProps, mapDispatchToProps)(EditDateTime),
);

const timeSlotModel = {
  eventId: PropTypes.string.isRequired,
  from: PropTypes.number.isRequired,
  to: PropTypes.number,
};

EditDateTime.propTypes = {
  prevPath: PropTypes.string,
  partners: PropTypes.arrayOf(PropTypes.object.isRequired).isRequired,

  mainAppointmentDraft: PropTypes.shape({
    appointmentType: PropTypes.shape({
      groups: PropTypes.arrayOf(PropTypes.object),
      id: PropTypes.string.isRequired,
    }).isRequired,
    shop: PropTypes.shape({
      id: PropTypes.string.isRequired,
    }).isRequired,
    partner: PropTypes.shape({}).isRequired,
    dateSlot: PropTypes.string,
    timeSlot: PropTypes.shape(timeSlotModel),
    id: PropTypes.string,
  }).isRequired,

  relatedAppointmentTimeSlot: PropTypes.shape(timeSlotModel),
  isMultipleAppointment: PropTypes.bool.isRequired,
  temporaryAppointment: PropTypes.shape({}).isRequired,
  history: PropTypes.shape({}).isRequired,
  slotErrorMessages: PropTypes.shape({
    type: PropTypes.shape({
      id: PropTypes.number.isRequired,
    }),
  }).isRequired,
  location: PropTypes.shape({}).isRequired,
  showWarningModal: PropTypes.bool.isRequired,
  isRemoteConfigEnabled: PropTypes.bool,
  title: PropTypes.string.isRequired,
  subtitleComponent: PropTypes.func,
  ShopsDropdown: PropTypes.func,
  shopList: PropTypes.arrayOf(PropTypes.shape({})),
  config: PropTypes.shape({
    partnerTitle: PropTypes.string,
  }).isRequired,
  onSaveDataHandler: PropTypes.func.isRequired,
  temporaryJourneyState: PropTypes.shape({}).isRequired,
  journeyState: PropTypes.shape({}).isRequired,
  hideEditDateTitle: PropTypes.bool,
  hideGoBackButton: PropTypes.bool,
  inStoreAppointmentTypeId: PropTypes.string,
  ChosenPartner: PropTypes.func,
  GroupsDropdown: PropTypes.func,

  dispatchSelectPartnersGroup: PropTypes.func.isRequired,
  dispatchApiCallFinished: PropTypes.func.isRequired,
  dispatchLoadPartners: PropTypes.func.isRequired,
  dispatchSelectPartner: PropTypes.func.isRequired,
  dispatchResetErrorMessagesWith: PropTypes.func.isRequired,
  dispatchRemoveSingleErrorMessage: PropTypes.func.isRequired,
  dispatchSetEditMode: PropTypes.func.isRequired,
  dispatchSaveTemporaryAppointment: PropTypes.func.isRequired,
  dispatchSetDraftAppointment: PropTypes.func.isRequired,
  dispatchSetJourneyState: PropTypes.func.isRequired,
  dispatchSaveTemporaryJourneyState: PropTypes.func.isRequired,
  dispatchSelectShop: PropTypes.func.isRequired,
  dispatchUpdateAppointmentType: PropTypes.func.isRequired,
  dispatchSetHeadlineBackButtonConfig: PropTypes.func.isRequired,
  dispatchClearErrorMessages: PropTypes.func.isRequired,
};

EditDateTime.defaultProps = {
  isRemoteConfigEnabled: false,
  subtitleComponent: null,
  ShopsDropdown: null,
  shopList: [],
  prevPath: undefined,
  hideEditDateTitle: false,
  hideGoBackButton: false,
  inStoreAppointmentTypeId: null,
  ChosenPartner: undefined,
  GroupsDropdown: undefined,
  relatedAppointmentTimeSlot: null,
};
